import type { ReactNode } from 'react';
import { formatDateTime } from '@/lib/format';
import { Panel, PanelHeader, Skeleton } from '@/components/ui';
import type { RevenueSheetState } from './use-revenue-tracker-sheet';

/**
 * When the mirror last ran, in words.
 *
 * 🔴 **THIS EXISTS BECAUSE A WORKING SYSTEM WAS REPORTED AS BROKEN.** Owner,
 * 2026-09-02, against the sibling Accounts Dashboard: a row was added, the
 * spreadsheet opened, the row was not in it, and the sync was reported dead.
 * Nothing was wrong — the worker ticks every ten minutes and had not ticked.
 * The screen said nothing either way, so *"saved, not yet mirrored"* and *"the
 * mirror is dead"* looked identical.
 *
 * ⚠️ **SO EVERY BRANCH SAYS SOMETHING AND NONE OF THEM IS A BLANK.** A missing
 * timestamp rendered as empty space reads as "fine", which is the exact failure
 * being closed, one layer up. `null` is NEVER SYNCED and is said in those
 * words; an absent key is "cannot tell" and is said in those words too, because
 * claiming "never" about a server that simply does not report it would be a
 * confident wrong answer of our own.
 */
export function revenueMirrorFreshnessText(sheet: RevenueSheetState): string {
  if (sheet.loading) return 'Checking when this was last synced…';
  if (!sheet.lastSyncedKnown) return 'Last synced: not reported by this server.';
  if (sheet.lastSyncedAt === null) return 'Not yet synced — this mirror has never run.';
  return `Last synced ${formatDateTime(sheet.lastSyncedAt)}.`;
}

/**
 * The mirror, with its freshness line as the CARD'S OWN FOOTER.
 *
 * 🔴 **THE FRESHNESS LINE MUST NOT FLOAT LOOSE ABOVE WHATEVER PANEL IS ON
 * SCREEN, AND IT MUST NOT RENDER ON THE TABLE VIEW AT ALL.** Owner-reported on
 * the Accounts Dashboard, 2026-09-02: bare grey text belonging to nothing, above
 * a table card, describing a document that was not there — *"it looks
 * submerged"*. The gating here is STRUCTURAL rather than a condition inside this
 * component: `RevenueTrackerPage` mounts it only on the sheet branch of its own
 * ternary, so there is no `view === 'sheet' &&` for anybody to delete.
 *
 * 🔴 **THERE IS NO `Sync now` BUTTON AND THERE MUST NOT BE ONE** — owner's
 * instruction, in his words: *"we only support auto sync … remove this sync
 * button section also no needed"*. **The freshness line is NOT part of that
 * removal.** The two arrived together on the sibling page and are easy to
 * mistake for one feature; they answer opposite questions. A button is *"run it
 * now"*; this line is *"has it run"* — and this line is the whole reason the
 * feature exists. Removing it rebuilds the original complaint with the fix
 * already in the file. A `sync` route may well exist server-side as an operator
 * path; that is not a reason to put a control in front of it.
 *
 * ── HOW IT GETS INSIDE A PANEL THIS FILE DOES NOT OWN ──────────────────────
 *
 * ⚠️ `TrackerSheetPanel` lives in `src/components/tracker-sheet/`, renders its
 * own `Panel`, and takes no children — so the line cannot be passed into it.
 * The card is JOINED instead: the panel's bottom rounding is dropped and the
 * strip below overlaps its bottom border by one pixel, so the two share a single
 * hairline and read as one card with a footer.
 *
 * ⚠️ **ONLY THE ROUNDING IS OVERRIDDEN — never the background or the border.**
 * `TrackerSheetPanel` goes `fixed inset-0` in full screen and paints the whole
 * window; a `bg-transparent` or `border-0` reaching it from out here would make
 * that overlay see-through, which is a regression in a state this file cannot
 * observe. `[&>section]` beats the panel's own `rounded-lg` on specificity, and
 * in full screen the panel already sets `rounded-none`, so the override is a
 * no-op there rather than a fight.
 */
export function RevenueMirrorPanel({
  sheet,
  children,
}: {
  sheet: RevenueSheetState;
  children: ReactNode;
}) {
  return (
    <div className="[&>section]:rounded-b-none">
      {children}
      <p className="m-0 -mt-px rounded-b-lg border border-border bg-surface px-4 py-2.5 text-xs text-fg-muted">
        {/*
         * ⚠️ Both halves, in one line. The cadence is what answers *"why is my
         * note not in the sheet yet"*; the timestamp is what separates *"it has
         * not ticked"* from *"it is dead"*. Neither is useful without the
         * other, which is why they were never two controls.
         */}
        {revenueMirrorFreshnessText(sheet)} EMERGE rewrites this sheet on its own, about every ten
        minutes.
      </p>
    </div>
  );
}

/**
 * The sheet's place, held while the mirror's address is still being read.
 *
 * ⚠️ Reached ONLY when the reader asked for `?view=sheet` and the answer has not
 * landed. The table is this page's default and needs no network answer, so an
 * ordinary arrival never sees this — it exists so a shared `?view=sheet` link
 * does not render the whole table and swap it for a frame a moment later.
 *
 * ⚠️ It is NOT rendered when the mirror turns out to be unconfigured: that is a
 * settled answer, not a pending one, and it lands on the table. A placeholder
 * resolving to nothing would be a shimmer promising a view that is never coming.
 *
 * ⚠️ **The header is `TrackerSheetPanel`'s header VERBATIM**, so nothing moves
 * when the real panel takes over. That is an invariant nothing checks and it has
 * already been broken once on the sibling page — the panel dropped a word and
 * this copy still said it, so for one edit the placeholder promised something
 * the frame did not. **Change both in the same edit.**
 */
export function RevenueSheetPlaceholderPanel() {
  return (
    <Panel>
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
      />
      <div aria-busy className="border-b border-border p-4">
        <Skeleton className="h-[70vh] min-h-[360px] w-full" />
      </div>
    </Panel>
  );
}
